/**
 * Author: Claude Code
 * Generation: Phaser3 animation key utilities
 * Version: v3.2.154
 * Modified: 2025-10-23 Beijing Time
 *
 * SpriteKeyUtils - 统一角色/武器帧键名生成与颜色映射，供 Manager/Player/Sequencer 复用
 */

import * as Phaser from 'phaser';
import { CharacterType, WeaponType, AnimationType, ANIMATION_FRAME_CONFIG } from '../spriteTypes';

// 颜色字符串 → CharacterType 数字枚举
export function colorToCharacterType(color: string): CharacterType {
  switch (String(color).toLowerCase()) {
    case 'green': return CharacterType.GREEN;
    case 'red': return CharacterType.RED;
    case 'purple': return CharacterType.PURPLE;
    case 'blue': return CharacterType.BLUE;
    case 'white': return CharacterType.WHITE;
    case 'black': return CharacterType.BLACK;
    case 'orange': return CharacterType.ORANGE;
    default: return CharacterType.GREEN;
  }
}

// CharacterType 数字枚举 → 颜色字符串
export function characterTypeToColor(type: CharacterType): string {
  const map: Record<number, string> = {
    [CharacterType.GREEN]: 'green',
    [CharacterType.RED]: 'red',
    [CharacterType.PURPLE]: 'purple',
    [CharacterType.BLUE]: 'blue',
    [CharacterType.WHITE]: 'white',
    [CharacterType.BLACK]: 'black',
    [CharacterType.ORANGE]: 'orange'
  } as any;
  return map[type] || 'green';
}

// 统一生成 Loader 规范的角色纹理键名：char-<num>-<anim>-<frame>
export function getCharacterFrameKey(
  characterType: CharacterType,
  animationType: AnimationType,
  frame: number
): string {
  const cfg = ANIMATION_FRAME_CONFIG[animationType];
  const frameStr = cfg?.useDoubleDigit ? String(frame).padStart(2, '0') : String(frame);
  return `char-${characterType}-${animationType}-${frameStr}`;
}

// 统一生成 Loader 规范的武器纹理键名：weapon-<type>-<anim>-<frame>
export function getWeaponFrameKey(
  weaponType: WeaponType,
  animationType: AnimationType,
  frame: number
): string {
  const cfg = ANIMATION_FRAME_CONFIG[animationType];
  const frameStr = cfg?.useDoubleDigit ? String(frame).padStart(2, '0') : String(frame);
  return `weapon-${weaponType}-${animationType}-${frameStr}`;
}

// 判断某一帧是否已加载进 Phaser 纹理缓存
export function isCharacterFrameLoaded(
  textures: Phaser.Textures.TextureManager,
  characterType: CharacterType,
  animationType: AnimationType,
  frame: number
): boolean {
  return textures.exists(getCharacterFrameKey(characterType, animationType, frame));
}

